import { z } from 'zod';

import { CURRENCIES } from '../constants/currency';
import {
  CEFR_LEVELS,
  MAX_PROFILE_LANGUAGES,
  MIN_PROFILE_LANGUAGES,
  type CefrLevel,
} from '../constants/languages';
import { nonEmptyString } from '../zod-common';

export const languageSkillSchema = z.object({
  code: z
    .string()
    .length(2, 'Code ISO 639-1 (2 lettres)')
    .regex(/^[a-z]{2}$/, 'Code en minuscules'),
  level: z.enum(CEFR_LEVELS),
});
export type LanguageSkill = z.infer<typeof languageSkillSchema>;

export const profileLanguagesSchema = z
  .array(languageSkillSchema)
  .min(MIN_PROFILE_LANGUAGES, `Au moins ${MIN_PROFILE_LANGUAGES} langue`)
  .max(MAX_PROFILE_LANGUAGES, `Au plus ${MAX_PROFILE_LANGUAGES} langues`)
  .refine(
    (langs) => new Set(langs.map((l) => l.code)).size === langs.length,
    'Doublons de langues interdits',
  );

export const MAX_TEAM_COMPOSITION_ENTRIES = 12;

export const teamCompositionEntrySchema = z.object({
  headcount: z.number().int().min(1, 'Au moins 1 personne').max(999, 'Effectif trop élevé'),
  role: nonEmptyString.max(60, 'Max 60 caractères'),
  languageCode: z
    .string()
    .length(2, 'Code ISO 639-1 (2 lettres)')
    .regex(/^[a-z]{2}$/, 'Code en minuscules'),
  level: z.enum(CEFR_LEVELS),
});
export type TeamCompositionEntry = z.infer<typeof teamCompositionEntrySchema>;

export interface TeamCompositionLevelGap {
  languageCode: string;
  declaredLevel: CefrLevel | null;
  composedLevel: CefrLevel;
}

export function teamCompositionLevelGaps(
  languages: Array<{ code: string; level: CefrLevel }>,
  composition: Array<{ languageCode: string; level: CefrLevel }>,
): TeamCompositionLevelGap[] {
  const declared = new Map(languages.map((entry) => [entry.code.toLowerCase(), entry.level]));
  const highestComposed = new Map<string, CefrLevel>();
  for (const entry of composition) {
    const code = entry.languageCode.toLowerCase();
    const current = highestComposed.get(code);
    if (!current || CEFR_LEVELS.indexOf(entry.level) > CEFR_LEVELS.indexOf(current)) {
      highestComposed.set(code, entry.level);
    }
  }
  const gaps: TeamCompositionLevelGap[] = [];
  for (const [code, composedLevel] of highestComposed) {
    const declaredLevel = declared.get(code) ?? null;
    if (
      declaredLevel === null ||
      CEFR_LEVELS.indexOf(declaredLevel) < CEFR_LEVELS.indexOf(composedLevel)
    ) {
      gaps.push({ languageCode: code, declaredLevel, composedLevel });
    }
  }
  return gaps;
}

export const teamCompositionSchema = z
  .array(teamCompositionEntrySchema)
  .max(MAX_TEAM_COMPOSITION_ENTRIES, `Au plus ${MAX_TEAM_COMPOSITION_ENTRIES} lignes`);

const portfolioUrlSchema = z
  .url('URL invalide')
  .refine((value) => /^https?:\/\//i.test(value), 'URL http(s) requise');
const portfolioUrlsSchema = z.array(portfolioUrlSchema).max(20, "Trop d'URLs");
const mediaKeySchema = z
  .string()
  .max(256)
  .regex(/^[^:]*$/, 'Clé invalide')
  .optional()
  .nullable();
const portfolioFileKeysSchema = z.array(z.string().min(1).max(300)).max(20, 'Trop de fichiers');
const skillTagsSchema = z.array(nonEmptyString.max(40)).max(30, 'Trop de compétences');
const domainsSchema = z.array(nonEmptyString.max(40)).max(15, 'Trop de domaines');

const legalIdentifierSchema = z
  .string()
  .trim()
  .min(4, 'Identifiant trop court')
  .max(32, 'Identifiant trop long');

const rateSchema = z.number().nonnegative('Doit être ≥ 0').max(1_000_000, 'Trop élevé').optional();

export const bidderIndividualProfileSchema = z.object({
  displayName: nonEmptyString.max(80, 'Max 80 caractères'),
  avatarKey: mediaKeySchema,
  cityLabel: z.string().max(80).optional().nullable(),
  description: z.string().max(2000).optional().nullable(),
  experiences: z.string().max(2000).optional().nullable(),
  skills: skillTagsSchema.default([]),
  domains: domainsSchema.default([]),
  languages: profileLanguagesSchema,
  portfolioUrls: portfolioUrlsSchema.default([]),
  portfolioFileKeys: portfolioFileKeysSchema.default([]),
  dailyRate: rateSchema,
  hourlyRate: rateSchema,
  rateCurrency: z.enum(CURRENCIES).optional(),
  availability: z.string().max(120).optional().nullable(),
  legalIdentifier: legalIdentifierSchema,
});
export type BidderIndividualProfileInput = z.infer<typeof bidderIndividualProfileSchema>;

export const bidderCompanyProfileSchema = z
  .object({
    companyName: nonEmptyString.max(120, 'Max 120 caractères'),
    companyLogoKey: mediaKeySchema,
    cityLabel: z.string().max(80).optional().nullable(),
    description: z.string().max(2000).optional().nullable(),
    skills: skillTagsSchema.default([]),
    domains: domainsSchema.default([]),
    languages: profileLanguagesSchema,
    portfolioUrls: portfolioUrlsSchema.default([]),
    portfolioFileKeys: portfolioFileKeysSchema.default([]),
    references: z
      .array(
        z.object({
          client: nonEmptyString.max(120),
          summary: z.string().max(400).optional().nullable(),
        }),
      )
      .max(20)
      .default([]),
    teamSizeMin: z.number().int().min(1).max(10_000).optional(),
    teamSizeMax: z.number().int().min(1).max(10_000).optional(),
    teamComposition: teamCompositionSchema.default([]),
    teamLanguageComposition: z.string().max(1000).optional().nullable(),
    primaryContactName: nonEmptyString.max(120),
    dailyRate: rateSchema,
    hourlyRate: rateSchema,
    rateCurrency: z.enum(CURRENCIES).optional(),
    availability: z.string().max(120).optional().nullable(),
    legalIdentifier: legalIdentifierSchema,
    honestyAcknowledged: z.literal(true, {
      error: 'Vous devez acquitter la déclaration de bonne foi',
    }),
  })
  .superRefine((value, ctx) => {
    for (const gap of teamCompositionLevelGaps(value.languages, value.teamComposition)) {
      ctx.addIssue({
        code: 'custom',
        path: ['languages'],
        message: `Composition de l'équipe : ${gap.languageCode.toUpperCase()} atteint ${gap.composedLevel}, le niveau déclaré doit être au moins équivalent (actuellement ${gap.declaredLevel ?? 'non déclaré'}).`,
      });
    }
  });
export type BidderCompanyProfileInput = z.infer<typeof bidderCompanyProfileSchema>;

export const setLanguagesSchema = z.object({
  languages: profileLanguagesSchema,
});
export type SetLanguagesInput = z.infer<typeof setLanguagesSchema>;

export const requesterProfileSchema = z.object({
  organizationName: z.string().max(120).optional().nullable(),
  legalIdentifier: z.string().max(32).optional().nullable(),
  cityLabel: z.string().max(80).optional().nullable(),
  description: z.string().max(2000).optional().nullable(),
});
export type RequesterProfileInput = z.infer<typeof requesterProfileSchema>;

export const uploadPortfolioFileSchema = z.object({
  filename: nonEmptyString.max(200),
  contentType: z
    .string()
    .regex(
      /^(application\/(pdf|zip|msword|vnd\.openxmlformats-officedocument\.[\w.-]+|vnd\.ms-(excel|powerpoint))|image\/(png|jpe?g|webp|gif|avif)|text\/plain)$/,
      'Type de fichier non autorisé',
    )
    .max(120),
  sizeBytes: z
    .number()
    .int()
    .min(1)
    .max(10 * 1024 * 1024, 'Fichier > 10 Mo interdit'),
});
export type UploadPortfolioFileInput = z.infer<typeof uploadPortfolioFileSchema>;

export const uploadProfileImageSchema = z.object({
  kind: z.enum(['AVATAR', 'LOGO']),
  filename: nonEmptyString.max(200),
  contentType: z
    .string()
    .regex(/^image\/(png|jpe?g|webp|gif|avif)$/, 'Image requise (png, jpg, webp, gif)')
    .max(120),
  sizeBytes: z
    .number()
    .int()
    .min(1)
    .max(5 * 1024 * 1024, 'Image > 5 Mo interdite'),
});
export type UploadProfileImageInput = z.infer<typeof uploadProfileImageSchema>;
